import { useEffect, useState } from 'react'
import SplitInvoiceForm from './SplitInvoiceForm'
import InvoiceUploadPanel from './InvoiceUploadPanel'
import {
  loadExpenseReport,
  ExpenseReport,
  EXPENSE_CATEGORY_LABEL,
  addExpense,
  deleteExpense,
} from '../../services/ledgersService'
import { supabase } from '../../lib/supabase'
import { User, Expense } from '../../types'
import { toast } from '../Toast'

/**
 * §6.5 — the expense report, and somewhere to record an expense.
 *
 * Before this the only cost capture in the platform was two number boxes on the
 * commissions admin screen labelled "Transport / Freight" and "Repairs /
 * Reconditioning". No category, no supplier, no invoice reference, no date —
 * so an expense could not be reconciled against a bill, and §6.1's "recalculate
 * if an expense is added before AR is paid" had nothing to hang off.
 */

const money = (n: number) =>
  `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const CATEGORIES = Object.keys(EXPENSE_CATEGORY_LABEL) as Expense['category'][]

const firstOfMonth = () => {
  const d = new Date()
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString().slice(0, 10)
}

export default function ExpensesTab({ user }: { user: User }) {
  const [splitting, setSplitting] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [rep, setRep] = useState<ExpenseReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [from, setFrom] = useState(firstOfMonth())
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10))
  const [deals, setDeals] = useState<{ id: string; label: string }[]>([])
  const [adding, setAdding] = useState(false)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  const [form, setForm] = useState({
    dealId: '',
    category: 'reconditioning' as Expense['category'],
    description: '',
    amount: '',
    supplier: '',
    invoiceRef: '',
    incurredOn: new Date().toISOString().slice(0, 10),
    capitalised: 'yes',
  })

  const splitPanel = splitting ? (
    <SplitInvoiceForm
      userId={user?.id}
      onCancel={() => setSplitting(false)}
      onSaved={() => {
        setSplitting(false)
        load()
      }}
    />
  ) : null

  const uploadPanel = uploading ? (
    <InvoiceUploadPanel
      userId={user?.id ?? null}
      onCancel={() => setUploading(false)}
      onSaved={() => {
        setUploading(false)
        load()
      }}
    />
  ) : null

  const load = () => {
    setLoading(true)
    loadExpenseReport(from, to)
      .then(setRep)
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load expenses'))
      .finally(() => setLoading(false))
  }

  useEffect(load, [from, to])

  useEffect(() => {
    supabase
      .from('deals')
      .select('id, vehicle_year, vehicle_make, vehicle_model, autograb_year, autograb_make, autograb_model, deal_status')
      .is('deleted_at', null)
      .not('deal_status', 'in', '("cancelled")')
      .order('created_at', { ascending: false })
      .limit(300)
      .then(({ data }) => {
        setDeals(
          (data || []).map((d: any) => ({
            id: d.id,
            label:
              [d.vehicle_year ?? d.autograb_year, d.vehicle_make ?? d.autograb_make, d.vehicle_model ?? d.autograb_model]
                .filter(Boolean)
                .join(' ') + ` · ${d.deal_status}`,
          }))
        )
      })
  }, [])

  const save = async () => {
    setNotice(null)
    if (!form.dealId) return setError('Pick the vehicle this cost belongs to.')
    const amount = parseFloat(form.amount)
    if (!amount || amount <= 0) return setError('Enter an amount.')
    setBusy(true)
    setError(null)
    try {
      const res = await addExpense(
        {
          dealId: form.dealId,
          category: form.category,
          description: form.description || undefined,
          amount,
          supplier: form.supplier || undefined,
          invoiceRef: form.invoiceRef || undefined,
          incurredOn: form.incurredOn,
          capitalised: form.capitalised === 'yes',
        },
        user.id
      )
      setNotice(res.message)
      toast.saved()
      setForm((f) => ({ ...f, amount: '', description: '', supplier: '', invoiceRef: '' }))
      load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not record the expense')
    } finally {
      setBusy(false)
    }
  }

  const remove = async (id: string, dealId: string) => {
    setBusy(true)
    try {
      await deleteExpense(id, dealId)
      load()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="rep-tabbody">
      <div className="rep-card">
        <div className="rep-card-head">
          <h3>Expenses</h3>
          <div className="rep-filters">
            <label>
              From <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label>
              To <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </label>
            <button className="rep-btn" onClick={() => setSplitting((v) => !v)}>
              {splitting ? 'Close split' : 'Split an invoice'}
            </button>
            <button className="rep-btn" onClick={() => setUploading((v) => !v)}>
              {uploading ? 'Close upload' : 'Upload an invoice'}
            </button>
            <button className="rep-btn" onClick={() => setAdding((v) => !v)}>
              {adding ? 'Close' : '+ Record an expense'}
            </button>
          </div>
          {splitPanel}
          {uploadPanel}
        </div>

        {adding && (
          <div className="rep-form">
            <div className="rep-form-grid">
              <label>
                Vehicle
                <select value={form.dealId} onChange={(e) => setForm((f) => ({ ...f, dealId: e.target.value }))}>
                  <option value="">Select a vehicle…</option>
                  {deals.map((d) => (
                    <option key={d.id} value={d.id}>{d.label}</option>
                  ))}
                </select>
              </label>
              <label>
                Category
                <select
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as Expense['category'] }))}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{EXPENSE_CATEGORY_LABEL[c]}</option>
                  ))}
                </select>
              </label>
              <label>
                Amount (inc GST)
                <input type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
              </label>
              <label>
                Date
                <input type="date" value={form.incurredOn} onChange={(e) => setForm((f) => ({ ...f, incurredOn: e.target.value }))} />
              </label>
              <label>
                Supplier
                <input value={form.supplier} onChange={(e) => setForm((f) => ({ ...f, supplier: e.target.value }))} />
              </label>
              <label>
                Invoice / bill ref
                <input value={form.invoiceRef} onChange={(e) => setForm((f) => ({ ...f, invoiceRef: e.target.value }))} />
              </label>
              <label>
                Capitalised
                <select value={form.capitalised} onChange={(e) => setForm((f) => ({ ...f, capitalised: e.target.value }))}>
                  <option value="yes">Yes — into landed cost</option>
                  <option value="no">No — period expense</option>
                </select>
              </label>
              <label className="wide">
                Description
                <input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
              </label>
            </div>
            <button className="rep-btn primary" onClick={save} disabled={busy}>
              {busy ? 'Saving…' : 'Record expense'}
            </button>
            <p className="rep-note">
              Capitalised costs go into the vehicle's landed cost and therefore into the stocktake,
              the margin and the commission. A period expense does not.
            </p>
          </div>
        )}

        {notice && <div className="rep-alert ok">{notice}</div>}
        {error && <div className="rep-alert error">{error}</div>}

        {loading ? (
          <div className="rep-loading">Loading expenses…</div>
        ) : !rep || rep.rows.length === 0 ? (
          <div className="rep-alert">
            No expenses recorded in this period. Costs entered on the commissions screen before this
            existed are still counted in landed cost, but they have no line items here.
          </div>
        ) : (
          <>
            <div className="rep-stats">
              <div className="rep-stat">
                <span className="rep-stat-label">Total</span>
                <span className="rep-stat-value">{money(rep.totals.amount)}</span>
              </div>
              <div className="rep-stat">
                <span className="rep-stat-label">GST component</span>
                <span className="rep-stat-value">{money(rep.totals.gst)}</span>
              </div>
              <div className="rep-stat">
                <span className="rep-stat-label">Capitalised</span>
                <span className="rep-stat-value">{money(rep.totals.capitalised)}</span>
                <span className="rep-stat-note">into landed cost</span>
              </div>
              <div className="rep-stat">
                <span className="rep-stat-label">Period expense</span>
                <span className="rep-stat-value">{money(rep.totals.expensed)}</span>
              </div>
            </div>

            <div className="rep-table-wrap">
              <table className="rep-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Vehicle</th>
                    <th>Category</th>
                    <th>Supplier</th>
                    <th>Ref</th>
                    <th className="num">Amount</th>
                    <th className="num">GST</th>
                    <th>Treatment</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rep.rows.map((r) => (
                    <tr key={r.id}>
                      <td>{r.incurred_on}</td>
                      <td>{r.vehicle}</td>
                      <td>{EXPENSE_CATEGORY_LABEL[r.category]}</td>
                      <td>{r.supplier || '—'}</td>
                      <td className="mono">{r.invoice_ref || '—'}</td>
                      <td className="num">{money(r.amount)}</td>
                      <td className="num">{money(r.gst_amount)}</td>
                      <td>{r.capitalised ? 'Capitalised' : 'Period'}</td>
                      <td>
                        <button className="rep-icon" title="Remove" onClick={() => remove(r.id, r.deal_id)} disabled={busy}>
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h4 className="rep-subhead">By category</h4>
            <div className="rep-table-wrap">
              <table className="rep-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th className="num">Items</th>
                    <th className="num">Amount</th>
                    <th className="num">GST</th>
                  </tr>
                </thead>
                <tbody>
                  {rep.byCategory.map((c) => (
                    <tr key={c.category}>
                      <td>{c.label}</td>
                      <td className="num">{c.count}</td>
                      <td className="num">{money(c.amount)}</td>
                      <td className="num">{money(c.gst)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
